import React, { useEffect, useState } from 'react';
import theme from './AnalyticsSettings.module.scss';
import { useAsyncState, useMergeState } from '@/utils/hooks';
import { getAnalyticsTheme, setAnalyticsTheme } from '@/services/analytics';
import { Button, ButtonBar, ColorPicker, ValidationForm, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { t } from '@lingui/core/macro';
import SimpleFormPage from '@/components/SimpleFormPage/SimpleFormPage';
import { Trans } from '@lingui/react/macro';
import { ReportTheme } from '@ygtechnologies/embedded-reports';

interface AnalyticsSettingsProps {
    profileId: number;
}

interface ColorField {
    field: keyof ReportTheme;
    name: string;
    description: string;
}

const AnalyticsSettings = ({ profileId }: AnalyticsSettingsProps) => {
    const [currentTheme] = useAsyncState(() => getAnalyticsTheme(profileId), [profileId], null);
    const [newTheme, mergeNewTheme, setNewTheme] = useMergeState<ReportTheme | null>(null);
    const [saving, setSaving] = useState(false);

    const fields: ColorField[] = [
        { field: 'color1', name: t`Color` + ' 1', description: t`Header and columns` },
        { field: 'color2', name: t`Color` + ' 2', description: t`Lines and secondary columns` },
        { field: 'color3', name: t`Color` + ' 3', description: t`Grid lines and borders` },
        { field: 'color4', name: t`Color` + ' 4', description: t`Alternative red` },
        { field: 'color5', name: t`Color` + ' 5', description: t`Alternative yellow` },
        { field: 'color6', name: t`Color` + ' 6', description: t`Alternative green` },
    ];

    useEffect(() => {
        setNewTheme({ ...currentTheme });
    }, [currentTheme, setNewTheme]);

    const handleSubmit = async () => {
        if (!newTheme) {
            return;
        }
        setSaving(true);
        try {
            await setAnalyticsTheme(profileId, newTheme);
            Toast.success(<Trans>Saved</Trans>);
        } finally {
            setSaving(false);
        }
    };

    if (!newTheme) {
        return null;
    }

    return (
        <SimpleFormPage>
            <ValidationForm onSubmit={handleSubmit} className="form columns-1 labels-20">
                <div className="form-label">
                    <Trans>Colors</Trans>
                    <div className="form-control-unstyled">
                        <div className={theme.colorsContainer}>
                            {fields.map((field) => (
                                <div key={field.field} className={theme.row}>
                                    <ColorPicker
                                        field={field.name}
                                        onChange={(v) => mergeNewTheme({ [field.field]: v })}
                                        value={newTheme[field.field] || ''}
                                        placeholder={t`Use default`}
                                    />
                                    <span className={theme.description}>{field.description}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <ButtonBar>
                    <ButtonBar.Right>
                        <Button type="submit" color="primary" loading={saving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
        </SimpleFormPage>
    );
};

export default AnalyticsSettings;
